import React, { useCallback, useEffect, useMemo } from 'react';
import AwpDropdown from '@awp/components/Common/DropDown/AwpDropdown';
import Table from '@awp/components/table';
import { sortByNumericThenAlpha } from '@awp/helpers/sorter';
import { selectRoles, selectUserTypes } from '@awp/stores/dropdownsSlice';
import { useSelector } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { hasAccess } from '@components/Authorize';
import { FORM_MODE, Permissions } from '@awp/constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { IconCaretDownFill, IconCaretRightFill } from '@awp/components/awpIcons';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint } from '@awp/helpers/UIHelper';
import 'react-tooltip/dist/react-tooltip.css';
import { Crud } from '@awp/shared/Crud';

const UsersTable = ({
  isMedium,
  isLarge,
  data,
  filteredData,
  setFilteredData,
  loading,
  selectedRoles,
  setSelectedRoles,
  selectedUserTypes,
  setSelectedUserTypes,
  selectedStatuses,
  setSelectedStatuses,
  search,
  setSearch,
  setMode,
  mode,
  setUser,
  defaultPageSize,
  setDefaultPageSize,
}) => {
  const rolesDropdown = useSelector(selectRoles);
  const userTypesDropdown = useSelector(selectUserTypes);

  const crud = new Crud('Registered Users', mode);

  const statusesDropdown = {
    options: [
      { value: 'Y', label: 'Active' },
      { value: 'N', label: 'Disabled' },
    ],
    status: null,
  };

  const hasSearch = (src) => {
    return src ? src.toLowerCase().includes(search.toLowerCase()) : false;
  };

  useEffect(() => {
    const roleNames = selectedRoles.map((r) => r.label);
    const userTypes = selectedUserTypes.map((t) => t.label);
    const statuses = selectedStatuses.map((s) => s.value);

    let filtered = data;

    if (roleNames.length !== 0) {
      filtered = filtered.filter((user) => {
        return user.Roles.filter((roles) => roleNames.includes(roles.label)).length > 0;
      });
    }

    if (userTypes.length !== 0) {
      filtered = filtered.filter((user) => userTypes.includes(user.UserType));
    }

    if (statuses.length !== 0) {
      filtered = filtered.filter((user) => statuses.includes(user.IsActive));
    }

    if (!isValueEmpty(search)) {
      filtered = filtered.filter(
        (user) =>
          hasSearch(user.FirstName) ||
          hasSearch(user.LastName) ||
          hasSearch(user.UserName) ||
          hasSearch(user.EmailAddress) ||
          hasSearch(user.Organization) ||
          hasSearch(user.RoleNames) ||
          hasSearch(user.UserType),
      );
    }

    setFilteredData(filtered);
  }, [selectedUserTypes, selectedRoles, selectedStatuses, search]);

  const cellComponentExp = ({ row }) => (
    // Use Cell to render an expander for each row.
    // We can use the getToggleRowExpandedProps prop-getter
    // to build the expander.
    <span {...row.getToggleRowExpandedProps()}>{row.isExpanded ? <IconCaretDownFill /> : <IconCaretRightFill />}</span>
  );

  const actionMenu = (row) => {
    return (
      <>
        <div>
          <i
            className="fas fa-edit mx-1 my-1 awp-img-btn"
            title="Edit"
            onClick={() => {
              setMode('edit');
              setUser(row.row.original);
            }}
          ></i>
          <i
            className="fas fa-eye mx-1 my-1 awp-img-btn"
            title="View"
            onClick={() => {
              setMode('view');
              setUser(row.row.original);
            }}
          ></i>
        </div>
      </>
    );
  };

  const tableColumn = () => {
    if (!isLarge) {
      TableColumns.unshift({
        // Make an expander cell
        Header: () => null, // No header
        accessor: 'ExpanderCell',
        id: 'expander', // It needs an ID
        Cell: cellComponentExp,
      });
    }
    return TableColumns;
  };

  const TableColumns = useMemo(
    () => [
      {
        Header: 'Last Name',
        accessor: 'LastName',
        style: { width: '10rem' },
        show: isLarge,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'First Name',
        accessor: 'FirstName',
        style: { width: '10rem' },
        show: isLarge,
        sortType: sortByNumericThenAlpha,
      },
      {
        Header: 'Last Login',
        accessor: 'LastLoginPst',
        style: { width: '7rem' },
        sortType: sortByNumericThenAlpha,
        show: isLarge,
      },
      {
        Header: 'User ID',
        accessor: 'UserName',
        sortType: sortByNumericThenAlpha,
        Cell: (row) => {
          const data = row.row.original;
          const id = `row-username-${data.Id}`;
          return (
            <>
              <span data-tooltip-content data-tooltip-id={id}>
                {row.value?.length > 9 ? row.value?.slice(0, 8) + '...' : row.value}
              </span>
              {toolTipInfo(row.row, id)}
            </>
          );
        },
      },
      {
        Header: 'User Type',
        accessor: 'UserType',
        style: { width: '7rem' },
        sortType: sortByNumericThenAlpha,
        show: isLarge,
      },
      {
        Header: 'Email',
        accessor: 'EmailAddress',
        sortType: sortByNumericThenAlpha,
        show: isMedium,
        Cell: (row) => {
          const data = row.row.original;
          const id = `row-email-${data.Id}`;
          return (
            <>
              <span data-tooltip-content data-tooltip-id={id}>
                {row.value?.length > 12 ? row.value?.slice(0, 9) + '...' : row.value}
              </span>
              {toolTipFullValue(row.value, id)}
            </>
          );
        },
      },
      {
        Header: 'Organization',
        accessor: 'Organization',
        sortType: sortByNumericThenAlpha,
        show: isLarge,
      },
      {
        Header: 'Status',
        accessor: 'IsActive',
        sortType: sortByNumericThenAlpha,
        Cell: (row) => {
          if (row.value === 'Y') {
            return <i className="fa fa-user-check  mx-1 my-1"></i>;
          } else {
            return <i className="fa fa-user-slash  mx-1 my-1"></i>;
          }
        },
      },
      {
        Header: 'Actions',
        accessor: 'Id',
        disableSortBy: true,
        Cell: actionMenu,
      },
    ],
    [isLarge, isMedium],
  );

  const toolTipFullValue = (value, id) => {
    return (
      <Tooltip
        id={id}
        place="bottom"
        type="dark"
        closeOnResize={true}
        render={() => (
          <>
            <p>
              <strong>{value}</strong>
            </p>
          </>
        )}
        disableStyleInjection="core"
      />
    );
  };

  const toolTipInfo = (row, id) => {
    const data = row.original;
    return (
      <Tooltip
        id={id}
        place="bottom"
        type="dark"
        closeOnResize={true}
        render={() => (
          <>
            <p>
              <strong>{data?.FullName ? data.FullName : data?.UserName}</strong>
              {data.IsActive === 'Y' ? (
                <strong className="ml-4 text-success" style={{ float: 'right' }}>
                  Active
                </strong>
              ) : (
                <strong className="ml-4 text-danger" style={{ float: 'right' }}>
                  Inactive
                </strong>
              )}
            </p>
            {data?.FullName ? (
              <p>
                <strong>{data?.UserName}</strong>
              </p>
            ) : (
              <></>
            )}
            <b>Role(s)</b>
            <br></br>
            {data.RoleNames}
          </>
        )}
        disableStyleInjection="core"
      />
    );
  };

  const renderRowSubComponent = useCallback(
    ({ row }) => (
      <table>
        <tbody>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              Last Name
            </td>
            <td className="awp_table_expend_row_details">{row.values['LastName']}</td>
          </tr>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              First Name
            </td>
            <td className="awp_table_expend_row_details">{row.values['FirstName']}</td>
          </tr>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              Last Login
            </td>
            <td className="awp_table_expend_row_details">{row.values['LastLoginPst']}</td>
          </tr>
          {!checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM) && (
            <tr>
              <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
                Email
              </td>
              <td className="awp_table_expend_row_details">
                <span data-tooltip-content data-tooltip-id={`row-email-${row.original.Id}`}>
                  {row.values['EmailAddress']?.length > 12
                    ? row.values['EmailAddress']?.slice(0, 9) + '...'
                    : row.value}
                </span>
                {toolTipFullValue(row.values['EmailAddress'], `row-email-${row.original.Id}`)}
              </td>
            </tr>
          )}
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              User Type
            </td>
            <td className="awp_table_expend_row_details">{row.values['UserType']}</td>
          </tr>
          <tr>
            <td className="awp_table_expend_row_details" style={{ fontWeight: 'bold' }}>
              Organization
            </td>
            <td className="awp_table_expend_row_details">{row.values['Organization']}</td>
          </tr>
        </tbody>
      </table>
    ),
    [],
  );

  return (
    <>
      <div className="d-flex">{crud.getHeader()}</div>
      <div className="container mb-3 pr-pl-0">
        <div className="row">
          <div className="col-sm-3">
            <AwpDropdown
              isMulti={true}
              selectedValues={selectedRoles}
              onChange={setSelectedRoles}
              data={rolesDropdown}
              title="Role"
            />
          </div>
          <div className="col-sm-3">
            <AwpDropdown
              isMulti={true}
              selectedValues={selectedUserTypes}
              onChange={setSelectedUserTypes}
              data={userTypesDropdown}
              title="User Types"
            />
          </div>
          <div className="col-sm-3">
            <AwpDropdown
              isMulti={true}
              selectedValues={selectedStatuses}
              onChange={setSelectedStatuses}
              data={statusesDropdown}
              title="Status"
            />
          </div>
          <div className="col-sm-3">
            <Form.Label>
              <b>Search</b>
            </Form.Label>
            <input
              className="form-control"
              placeholder="Search..."
              id="user_text_search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            ></input>
          </div>
        </div>
      </div>
      <div className="user-table-override">
        <Table
          columns={tableColumn()}
          data={filteredData}
          fetchData={() => {}}
          loading={loading}
          search=""
          filters={[]}
          dataTriggers={[]}
          renderRowSubComponent={renderRowSubComponent}
          defaultSortBy={'LastName'}
          defaultPageSize={defaultPageSize}
          setDefaultPageSize={setDefaultPageSize}
        />
      </div>
      {hasAccess([Permissions.AdminDefault]) && (
        <div className="my-3">
          <Row>
            <Col sm={3} md={4}>
              <Button
                className="mr-2 my-3"
                variant="primary"
                onClick={() => {
                  setMode(FORM_MODE.CREATE);
                }}
                disabled={loading}
              >
                Add Aps Client
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default UsersTable;
